import React, { useState } from "react";
import { api } from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { toast } from "sonner";
import { Sparkles, Copy, Check } from "lucide-react";

export default function AIGenerator() {
  const { t, lang } = useI18n();
  const [form, setForm] = useState({ generation_type: "job_ad", job_title: "", country: "", language: lang, details: "" });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const generate = async () => {
    if (!form.job_title) return toast.error("Job title required");
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/ai/generate", form);
      setResult(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally { setLoading(false); }
  };

  const copy = () => {
    if (!result) return;
    const text = `${result.content}\n\n${result.hashtags.join(" ")}`;
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="space-y-6" data-testid="ai-root">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">{t("ai_generator")}</div>
        <h1 className="font-display text-4xl font-bold tracking-tight">{t("ai_generator")}</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <div className="swiss-card p-6 lg:col-span-2 space-y-4">
          <div>
            <label className="text-xs uppercase tracking-[0.15em] text-zinc-500">{t("generation_type")}</label>
            <div className="grid grid-cols-3 gap-1 mt-2">
              {["job_ad", "facebook_post", "tiktok_script"].map((g) => (
                <button
                  key={g}
                  data-testid={`gen-type-${g}`}
                  onClick={() => setForm({ ...form, generation_type: g })}
                  className={`px-2 py-2 text-xs uppercase tracking-wider border ${form.generation_type === g ? "klein-blue border-[#002FA7]" : "bg-white border-zinc-300 hover:bg-zinc-50"}`}
                  style={{ borderRadius: 2 }}
                >
                  {t(g)}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.15em] text-zinc-500">{t("job_title")}</label>
            <input data-testid="ai-job-title" placeholder={t("job_title_ph")} className="mt-1 w-full border border-zinc-300 px-3 py-2.5" style={{borderRadius: 2}} value={form.job_title} onChange={(e) => setForm({...form, job_title: e.target.value})}/>
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.15em] text-zinc-500">{t("country")}</label>
            <input data-testid="ai-country" placeholder={t("country_ph")} className="mt-1 w-full border border-zinc-300 px-3 py-2.5" style={{borderRadius: 2}} value={form.country} onChange={(e) => setForm({...form, country: e.target.value})}/>
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.15em] text-zinc-500">{t("select_language")}</label>
            <select data-testid="ai-lang" className="mt-1 w-full border border-zinc-300 px-3 py-2.5 bg-white" style={{borderRadius: 2}} value={form.language} onChange={(e) => setForm({...form, language: e.target.value})}>
              <option value="fr">Français</option>
              <option value="en">English</option>
              <option value="hu">Magyar</option>
              <option value="it">Italiano</option>
              <option value="de">Deutsch</option>
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.15em] text-zinc-500">{t("details")}</label>
            <textarea data-testid="ai-details" rows={3} placeholder={t("details_ph")} className="mt-1 w-full border border-zinc-300 px-3 py-2.5" style={{borderRadius: 2}} value={form.details} onChange={(e) => setForm({...form, details: e.target.value})}/>
          </div>
          <button data-testid="ai-generate" disabled={loading} onClick={generate} className="btn-primary w-full inline-flex items-center justify-center gap-2">
            <Sparkles size={16}/> {loading ? "…" : t("generate")}
          </button>
        </div>

        <div className="swiss-card lg:col-span-3 min-h-[420px] relative overflow-hidden">
          {loading && <div className="tracing-beam absolute inset-0 pointer-events-none"/>}
          <div className="p-6 h-full flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">Result</div>
              {result && (
                <button onClick={copy} data-testid="ai-copy" className="inline-flex items-center gap-1 text-xs px-3 py-1.5 border border-zinc-300 hover:bg-zinc-50" style={{borderRadius: 2}}>
                  {copied ? <Check size={14}/> : <Copy size={14}/>} {copied ? t("copied") : t("copy")}
                </button>
              )}
            </div>
            {!result && !loading && (
              <div className="flex-1 flex items-center justify-center text-zinc-400 text-sm text-center p-8">
                <div>
                  <Sparkles size={28} className="mx-auto mb-3 text-zinc-300"/>
                  {t("generate")} → {t("copy")}
                </div>
              </div>
            )}
            {loading && (
              <div className="flex-1 flex items-center justify-center text-zinc-500 text-sm">
                <div className="font-display text-xl font-bold tracking-tight animate-pulse">Generating…</div>
              </div>
            )}
            {result && (
              <div className="flex-1 overflow-auto">
                <pre data-testid="ai-result-content" className="whitespace-pre-wrap text-sm leading-relaxed font-sans">{result.content}</pre>
                {result.hashtags.length > 0 && (
                  <div className="mt-5 pt-4 border-t border-zinc-200">
                    <div className="text-xs uppercase tracking-[0.15em] text-zinc-500 mb-2">{t("hashtags")}</div>
                    <div className="flex gap-1.5 flex-wrap" data-testid="ai-hashtags">
                      {result.hashtags.map((h, i) => (
                        <span key={i} className="text-xs px-2 py-1 bg-[#002FA7]/10 text-[#002FA7] font-medium" style={{borderRadius: 2}}>{h}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
